import type { ZodError } from 'zod'

export const ASSISTANT_TOOL_ERROR_CODES = {
  noActiveBrowserInstance: 'NO_ACTIVE_BROWSER_INSTANCE',
  localBrowserSessionDisconnected: 'LOCAL_BROWSER_SESSION_DISCONNECTED',
  toolCancelled: 'TOOL_CANCELLED',
  noCurrentProject: 'NO_CURRENT_PROJECT',
  workspaceNotReady: 'WORKSPACE_NOT_READY',
  workspacePermissionDenied: 'WORKSPACE_PERMISSION_DENIED',
  mediaNotFound: 'MEDIA_NOT_FOUND',
  invalidToolInput: 'INVALID_TOOL_INPUT',
  unsupportedRuntimeCapability: 'UNSUPPORTED_RUNTIME_CAPABILITY',
  authRequired: 'AUTH_REQUIRED',
  subscriptionRequired: 'SUBSCRIPTION_REQUIRED',
  featureNotIncludedInPlan: 'FEATURE_NOT_INCLUDED_IN_PLAN',
  quotaExceeded: 'QUOTA_EXCEEDED',
  apiAccessDenied: 'API_ACCESS_DENIED',
  exportNotSupported: 'EXPORT_NOT_SUPPORTED',
  exportFailed: 'EXPORT_FAILED',
  ttsFailed: 'TTS_FAILED',
  transcriptionFailed: 'TRANSCRIPTION_FAILED',
  timelineMutationFailed: 'TIMELINE_MUTATION_FAILED',
  toolExecutionFailed: 'TOOL_EXECUTION_FAILED',
} as const

export type AssistantToolErrorCode =
  (typeof ASSISTANT_TOOL_ERROR_CODES)[keyof typeof ASSISTANT_TOOL_ERROR_CODES]

export interface AssistantToolErrorShape {
  code: AssistantToolErrorCode
  message: string
  retryable?: boolean
  details?: Record<string, unknown>
}

export class AssistantToolError extends Error implements AssistantToolErrorShape {
  readonly code: AssistantToolErrorCode
  readonly retryable: boolean
  readonly details?: Record<string, unknown>
  override readonly cause?: unknown

  constructor(
    code: AssistantToolErrorCode,
    message: string,
    options?: {
      retryable?: boolean
      details?: Record<string, unknown>
      cause?: unknown
    },
  ) {
    super(message)
    this.name = 'AssistantToolError'
    this.code = code
    this.retryable = options?.retryable ?? false
    this.details = options?.details
    this.cause = options?.cause
  }

  toJSON(): AssistantToolErrorShape {
    return {
      code: this.code,
      message: this.message,
      retryable: this.retryable,
      details: this.details,
    }
  }
}

export function isAssistantToolError(error: unknown): error is AssistantToolError {
  return error instanceof AssistantToolError
}

export function createInvalidToolInputError(error: ZodError): AssistantToolError {
  return new AssistantToolError(
    ASSISTANT_TOOL_ERROR_CODES.invalidToolInput,
    'Invalid assistant tool input.',
    {
      details: {
        issues: error.issues.map((issue) => ({
          path: issue.path.join('.'),
          message: issue.message,
          code: issue.code,
        })),
      },
    },
  )
}

function classifyAssistantToolError(error: Error): AssistantToolError {
  const message = error.message

  if (error.name === 'AbortError' || /abort|cancelled|canceled/i.test(message)) {
    return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.toolCancelled, message, {
      cause: error,
    })
  }

  if (/local browser session disconnected|browser bridge completed/i.test(message)) {
    return new AssistantToolError(
      ASSISTANT_TOOL_ERROR_CODES.localBrowserSessionDisconnected,
      message,
      {
        cause: error,
        retryable: true,
      },
    )
  }

  if (/current project|project context/i.test(message)) {
    return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.noCurrentProject, message, {
      cause: error,
    })
  }

  if (/workspace root is not set|workspace.*before any storage operation/i.test(message)) {
    return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.workspaceNotReady, message, {
      cause: error,
    })
  }

  if (/notallowederror|permission denied|denied/i.test(message)) {
    return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.workspacePermissionDenied, message, {
      cause: error,
    })
  }

  if (/project not found|media not found/i.test(message)) {
    return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.mediaNotFound, message, {
      cause: error,
    })
  }

  if (
    /transcrib|caption|whisper|decoder|audio track|speech[- ]to[- ]text|stt|cross attentions|output_attentions|extract timestamps/i.test(
      message,
    )
  ) {
    return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.transcriptionFailed, message, {
      cause: error,
      retryable: true,
    })
  }

  if (/webcodec|codec|browser.*support/i.test(message)) {
    return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.exportNotSupported, message, {
      cause: error,
    })
  }

  if (/export/i.test(message)) {
    return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.exportFailed, message, {
      cause: error,
      retryable: true,
    })
  }

  if (/tts|speech|voice/i.test(message)) {
    return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.ttsFailed, message, {
      cause: error,
      retryable: true,
    })
  }

  if (/timeline|track|transition|clip/i.test(message)) {
    return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.timelineMutationFailed, message, {
      cause: error,
    })
  }

  return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.toolExecutionFailed, message, {
    cause: error,
  })
}

export function normalizeAssistantToolError(error: unknown): AssistantToolError {
  if (isAssistantToolError(error)) {
    return error
  }

  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    const toolAccessError = error as { code?: unknown; message: string }
    switch (toolAccessError.code) {
      case ASSISTANT_TOOL_ERROR_CODES.authRequired:
        return new AssistantToolError(ASSISTANT_TOOL_ERROR_CODES.authRequired, toolAccessError.message)
      case 'FEATURE_NOT_INCLUDED':
        return new AssistantToolError(
          ASSISTANT_TOOL_ERROR_CODES.featureNotIncludedInPlan,
          toolAccessError.message,
        )
      case ASSISTANT_TOOL_ERROR_CODES.quotaExceeded:
        return new AssistantToolError(
          ASSISTANT_TOOL_ERROR_CODES.quotaExceeded,
          toolAccessError.message,
        )
      case ASSISTANT_TOOL_ERROR_CODES.apiAccessDenied:
        return new AssistantToolError(
          ASSISTANT_TOOL_ERROR_CODES.apiAccessDenied,
          toolAccessError.message,
        )
      default:
        break
    }
  }

  if (error instanceof Error) {
    return classifyAssistantToolError(error)
  }

  return new AssistantToolError(
    ASSISTANT_TOOL_ERROR_CODES.toolExecutionFailed,
    String(error ?? 'Unknown assistant tool error.'),
    {
      details: { rawError: error },
    },
  )
}
